import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/admin.css';
import ConfigPanel from '../../admin/ConfigPanel';
import AccountPage from '../../admin/AccountPage';
import EmailPreferences from '../../admin/EmailPreferences';
import TenantMatrixPage from '../../pages/admin/TenantMatrixPage';
import IncomeStatementPage from '../../admin/IncomeStatementPage'; // ✅ NEW
import CustomerInfoPage from '../../admin/CustomerInfoPage'; // ✅ NEW

export default function AdminPanel() {
  const [tenants, setTenants] = useState([]);
  const [payments, setPayments] = useState([]);
  const [accountRegistry, setAccountRegistry] = useState({});
  const [activeModule, setActiveModule] = useState('overview');
  const navigate = useNavigate();

  useEffect(() => {
    const storedTenants = JSON.parse(localStorage.getItem('tenants') || '[]');
    setTenants(storedTenants);

    const storedPayments = JSON.parse(localStorage.getItem('paymentLogs') || '[]');
    const sorted = storedPayments.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    setPayments(sorted);

    const registry = JSON.parse(localStorage.getItem('accountRegistry') || '{}');
    setAccountRegistry(registry);
  }, []);

  const toggleStatus = (index) => {
    const updated = [...tenants];
    updated[index].status = updated[index].status === 'active' ? 'inactive' : 'active';
    setTenants(updated);
    localStorage.setItem('tenants', JSON.stringify(updated));
  };

  const togglePayment = (index) => {
    const updated = [...tenants];
    updated[index].paymentStatus = updated[index].paymentStatus === 'paid' ? 'unpaid' : 'paid';
    setTenants(updated);
    localStorage.setItem('tenants', JSON.stringify(updated));
  };

  const renderModule = () => {
    switch (activeModule) {
      case 'config':
        return <ConfigPanel />;
      case 'ats':
        return <AccountPage />;
      case 'email':
        return <EmailPreferences />;
      case 'matrix':
        return <TenantMatrixPage />;
      case 'income':
        return <IncomeStatementPage />;
      case 'customer':
        return <CustomerInfoPage />; // ✅ NEW
      default:
        return (
          <OverviewSection
            tenants={tenants}
            payments={payments}
            accountRegistry={accountRegistry}
            toggleStatus={toggleStatus}
            togglePayment={togglePayment}
            navigate={navigate}
          />
        );
    }
  };

  return (
    <div className="admin-panel max-w-6xl mx-auto bg-gray-50 p-6 rounded shadow mt-10">
      <h2 className="text-2xl font-bold mb-4">🛠️ Admin Panel</h2>

      <div className="mb-6">
        <label className="block mb-2 text-sm font-medium text-gray-700">Select Admin Tool:</label>
        <select
          value={activeModule}
          onChange={(e) => setActiveModule(e.target.value)}
          className="border p-0 rounded bg-white"
        >
          <option value="overview">Tenant Overview</option>
          <option value="matrix">Tenant Matrix</option>
          <option value="config">Config Panel</option>
          <option value="ats">Account Overview</option>
          <option value="income">Income Statement</option>
          <option value="customer">Customer Info</option> {/* ✅ NEW */}
          <option value="email">Email Preferences</option>
        </select>
      </div>

      {renderModule()}
    </div>
  );
}

function OverviewSection({
  tenants,
  payments,
  accountRegistry,
  toggleStatus,
  togglePayment,
  navigate
}) {
  return (
    <>
      <section className="mb-10">
        <h3 className="text-xl font-semibold mb-2">Tenant Overview</h3>
        {tenants.length === 0 ? (
          <p>No tenants found.</p>
        ) : (
          <table className="w-full border">
            <thead>
              <tr className="bg-gray-200">
                <th className="p-2">Tenant ID</th>
                <th className="p-2">Company</th>
                <th className="p-2">Status</th>
                <th className="p-2">Payment</th>
                <th className="p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant, index) => {
                const account =
                  Object.values(accountRegistry).find((acc) => acc.tenantId === tenant.tenantId) ||
                  {};

                return (
                  <tr key={tenant.tenantId} className="border-t">
                    <td className="p-2">{tenant.tenantId}</td>
                    <td className="p-2">{tenant.companyName}</td>
                    <td className="p-2">{tenant.status || 'pending'}</td>
                    <td className="p-2">{tenant.paymentStatus || 'unpaid'}</td>
                    <td className="p-2 space-x-2">
                      <button
                        onClick={() => toggleStatus(index)}
                        className="bg-blue-500 text-white px-2 py-1 rounded"
                      >
                        Toggle Status
                      </button>
                      <button
                        onClick={() => togglePayment(index)}
                        className="bg-green-500 text-white px-2 py-1 rounded"
                      >
                        Toggle Payment
                      </button>
                      <button
                        onClick={() =>
                          navigate('/payment', {
                            state: {
                              tenantId: tenant.tenantId,
                              companyName: tenant.companyName,
                              amount: account.amount || 0,
                              currency: account.currency || 'BDT',
                              method: account.method || 'Card',
                              selectedModules: account.selectedModules || [],
                              billingCycle: account.billingCycle || 'monthly',
                              discountRate: account.discountRate || 0,
                              vatRate: account.vatRate || 0
                            }
                          })
                        }
                        className="bg-indigo-600 text-white px-2 py-1 rounded"
                      >
                        Trigger Payment
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <hr className="my-8 border-gray-300" />

      <section className="payment-logs">
        <h3 className="text-xl font-semibold mb-2">Recent Payments</h3>
        {payments.length === 0 ? (
          <p>No payments recorded yet.</p>
        ) : (
          <table className="w-full border">
            <thead>
              <tr className="bg-gray-200">
                <th className="p-2">Tenant</th>
                <th className="p-2">Company</th>
                <th className="p-2">Amount</th>
                <th className="p-2">Currency</th>
                <th className="p-2">Method</th>
                <th className="p-2">Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p, index) => (
                <tr key={index} className="border-t">
                  <td className="p-2">{p.tenantId}</td>
                  <td className="p-2">{p.companyName}</td>
                  <td className="p-2">{p.amount}</td>
                  <td className="p-2">{p.currency}</td>
                  <td className="p-2">{p.method}</td>
                  <td className="p-2">{new Date(p.timestamp).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
